import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';

dayjs.extend(minMax);

export const FIXED_INFLATION_RATES = {
  '2022': { ufe: 97.72, tufe: 64.27 },
  '2023': { ufe: 44.22, tufe: 64.77 },
  '2024': { ufe: 28.52, tufe: 44.38 }
};

export const convertDbRateToFrontendFormat = (dbRate) => {
  const rateMap = {
    0: '0',
    1: 'ufe',
    2: 'tufe',
    3: 'ufetufe',
    5: '0.25',
    6: 'ufetufe'
  };
  return rateMap[dbRate] || '0.25';
};

export const getInflationRate = (rateType, date, inflationRates = {}) => {
  const dateObj = dayjs(date);
  const year = dateObj.year().toString();

  if (typeof rateType === 'number' || (typeof rateType === 'string' && /^\d+$/.test(rateType))) {
    const dbRate = parseInt(rateType);
    if (dbRate === 0) return 0;
    if (dbRate === 5) return 0.25;
    if (dbRate === 6) {
      if (FIXED_INFLATION_RATES[year]) {
        return (FIXED_INFLATION_RATES[year].ufe + FIXED_INFLATION_RATES[year].tufe) / 200;
      }
      if (inflationRates[year]) {
        return (inflationRates[year].ufe + inflationRates[year].tufe) / 200;
      }
      return 0.25;
    }
    rateType = convertDbRateToFrontendFormat(dbRate);
  }

  if (FIXED_INFLATION_RATES[year]) {
    let rate;
    switch (rateType) {
      case 'ufe':
        rate = FIXED_INFLATION_RATES[year].ufe / 100;
        break;
      case 'tufe':
        rate = FIXED_INFLATION_RATES[year].tufe / 100;
        break;
      case 'ufetufe':
        rate = (FIXED_INFLATION_RATES[year].ufe + FIXED_INFLATION_RATES[year].tufe) / 200;
        break;
      default:
        rate = parseFloat(rateType) || 0;
    }
    return rate;
  }

  if (!inflationRates[year]) {
    return 0.25;
  }

  let rate;
  switch (rateType) {
    case 'ufe':
      rate = inflationRates[year].ufe / 100;
      break;
    case 'tufe':
      rate = inflationRates[year].tufe / 100;
      break;
    case 'ufetufe':
      rate = (inflationRates[year].ufe + inflationRates[year].tufe) / 200;
      break;
    default:
      rate = parseFloat(rateType) || 0;
  }
  return rate;
};

export const calculateMonthlyAmount = (contract, targetDate, getInflationRateFn) => {
  const adjustedTargetDate = dayjs(targetDate).add(1, 'month');

  if (!contract) return 0;
  
  const contractStartDate = dayjs(contract.startDate);
  const contractEndDate = contract.endDate ? dayjs(contract.endDate) : null;

  if (adjustedTargetDate.isBefore(contractStartDate) || (contractEndDate && adjustedTargetDate.isAfter(contractEndDate))) {
    return 0;
  }

  if (contract.hasOwnProperty('active') && contract.active === false) {
    return 0;
  }

  const start = dayjs(contract.startDate);
  const target = dayjs(targetDate);
  const monthlyBase = contract.amount / 12;

  const startYear = start.year();
  const startMonth = start.month();
  const startDay = start.date();
  const targetYear = target.year();
  const targetMonth = target.month();
  const targetDay = target.date();

  let yearsPassed = targetYear - startYear;
  if (targetMonth < startMonth || (targetMonth === startMonth && targetDay < startDay)) {
    yearsPassed--;
  }
  yearsPassed = Math.max(0, yearsPassed);

  let finalAmount;
  const rateValue = contract.rate;
  const isDbFormat = typeof rateValue === 'number' || (typeof rateValue === 'string' && /^\d+$/.test(rateValue));

  if (isDbFormat) {
    const dbRate = parseInt(rateValue);
    if (dbRate === 0) {
      finalAmount = monthlyBase;
    } else if (dbRate === 5) {
      const rate = 0.25;
      finalAmount = monthlyBase * Math.pow(1 + rate, yearsPassed);
    } else if (dbRate === 6) {
      let amount = monthlyBase;
      for (let year = 1; year <= yearsPassed; year++) {
        const increaseYear = startYear + year;
        const previousYear = dayjs(`${increaseYear - 1}-12-31`);
        const yearRate = getInflationRateFn(3, previousYear);
        amount *= (1 + yearRate);
      }
      finalAmount = amount;
    } else {
      let amount = monthlyBase;
      for (let year = 1; year <= yearsPassed; year++) {
        const increaseYear = startYear + year;
        const previousYear = dayjs(`${increaseYear - 1}-12-31`);
        const yearRate = getInflationRateFn(dbRate, previousYear);
        amount *= (1 + yearRate);
      }
      finalAmount = amount;
    }
  } else if (typeof contract.rate === 'string' && ['ufe', 'tufe', 'ufetufe'].includes(contract.rate)) {
    let amount = monthlyBase;
    for (let year = 1; year <= yearsPassed; year++) {
      const increaseYear = startYear + year;
      const previousYear = dayjs(`${increaseYear - 1}-12-31`);
      const yearRate = getInflationRateFn(contract.rate, previousYear);
      amount *= (1 + yearRate);
    }
    finalAmount = amount;
  } else {
    const rate = parseFloat(contract.rate) || 0;
    finalAmount = monthlyBase * Math.pow(1 + rate, yearsPassed);
  }

  return finalAmount;
};

export const calculateProjectionForDates = (dates, bankContracts, rentContracts, getInflationRateFn) => {
  const projection = [];
  let cumulativeProfit = 0;

  if (dates.length === 0) {
    console.warn('Tarih dizisi boş!');
    return [];
  }

  dates.forEach((date) => {
    const year = dayjs(date).year();
    const month = dayjs(date).month() + 1;

    let bankAmount = 0;
    const bankDetails = [];

    bankContracts.forEach(contract => {
      if (contract.active !== false) {
        const contractAmount = calculateMonthlyAmount(contract, date, getInflationRateFn);
        bankAmount += contractAmount;
        if (contractAmount > 0) {
          bankDetails.push({
            name: contract.name,
            amount: contractAmount
          });
        }
      }
    });

    let rentAmount = 0;
    const rentDetails = [];

    rentContracts.forEach(contract => {
      if (!contract.paymentType || contract.paymentType === 'fixed') {
        const contractAmount = calculateMonthlyAmount(contract, date, getInflationRateFn);
        rentAmount += contractAmount;
        if (contractAmount > 0) {
          rentDetails.push({
            name: contract.name,
            amount: contractAmount
          });
        }
      }
    });

    let remainingBankAmount = bankAmount;
    const activeBankCount = bankDetails.length;

    for (const contract of rentContracts) {
      if (contract.paymentType === 'bankBased') {
        if (bankDetails.length === 0 || remainingBankAmount <= 0) {
          rentDetails.push({
            name: contract.name,
            amount: 0,
            paid: 0,
            debt: 0,
            skipped: true
          });
          continue;
        }

        const baseContractAmount = calculateMonthlyAmount(contract, date, getInflationRateFn);
        const actualContractAmount = baseContractAmount * activeBankCount;
        let paidAmount = 0;
        if (remainingBankAmount < actualContractAmount) {
          paidAmount = remainingBankAmount;
        } else {
          paidAmount = actualContractAmount;
        }
        remainingBankAmount -= paidAmount;
        rentAmount += actualContractAmount;

        if (actualContractAmount > 0) {
          rentDetails.push({
            name: contract.name,
            amount: actualContractAmount,
            baseAmount: baseContractAmount,
            bankMultiplier: activeBankCount,
            paid: paidAmount,
            debt: actualContractAmount - paidAmount
          });
        }
      }
    }

    const profit = bankAmount - rentAmount;
    cumulativeProfit += profit;

    projection.push({
      date: date,
      year: year,
      month: month,
      bankAmount: bankAmount,
      rentAmount: rentAmount,
      profit: profit,
      cumulativeProfit: cumulativeProfit,
      bankDetails: bankDetails,
      rentDetails: rentDetails
    });
  });

  return projection;
};

export const calculateDateRange = (bankContracts, rentContracts) => {
  if (bankContracts.length === 0 && rentContracts.length === 0) {
    return {
      // 13.08 düzeltmesi: sabit (bayat) pencere yerine bugüne göre dinamik pencere
      startDate: dayjs().subtract(1, 'year').startOf('month'),
      endDate: dayjs().add(3, 'year').startOf('month')
    };
  }

  const allContracts = [...bankContracts, ...rentContracts];
  const validContracts = allContracts.filter(contract => {
    if (!contract || !contract.startDate) return false;
    const startDate = dayjs(contract.startDate);
    return startDate.isValid();
  });

  if (validContracts.length === 0) {
    console.warn('Geçerli tarihli sözleşme yok!');
    return {
      // 13.08 düzeltmesi: sabit (bayat) pencere yerine bugüne göre dinamik pencere
      startDate: dayjs().subtract(1, 'year').startOf('month'),
      endDate: dayjs().add(3, 'year').startOf('month')
    };
  }

  const startDates = validContracts.map(contract => {
    const date = dayjs(contract.startDate);
    if (!date.isValid()) {
      console.warn('Geçersiz başlangıç tarihi:', contract.startDate);
      return null;
    }
    return date;
  }).filter(date => date !== null);

  const endDates = validContracts.map(contract => {
    if (contract.endDate) {
      const date = dayjs(contract.endDate);
      if (!date.isValid()) {
        console.warn('Geçersiz bitiş tarihi:', contract.endDate);
        return dayjs('2100-12-31');
      }
      return date;
    }
    return dayjs('2100-12-31');
  });

  if (startDates.length === 0) {
    console.error('Geçerli başlangıç tarihi bulunamadı!');
    return {
      // 13.08 düzeltmesi: sabit (bayat) pencere yerine bugüne göre dinamik pencere
      startDate: dayjs().subtract(1, 'year').startOf('month'),
      endDate: dayjs().add(3, 'year').startOf('month')
    };
  }

  let startDate;
  let endDate;
  
  try {
    const minStartDate = dayjs.min(startDates);
    if (!minStartDate || !minStartDate.isValid()) {
      throw new Error('Geçersiz min start date');
    }
    startDate = minStartDate.subtract(1, 'month');
    
    const realEndDates = endDates.filter(date => date && date.isValid() && date.year() < 2100);
    if (realEndDates.length > 0) {
      const maxEndDate = dayjs.max(realEndDates);
      if (!maxEndDate || !maxEndDate.isValid()) {
        throw new Error('Geçersiz max end date');
      }
      endDate = maxEndDate.subtract(1, 'month');
    } else {
      endDate = dayjs().add(5, 'year').endOf('year');
    }
  } catch (error) {
    console.error('Tarih hesaplama hatası:', error);
    return {
      // 13.08 düzeltmesi: sabit (bayat) pencere yerine bugüne göre dinamik pencere
      startDate: dayjs().subtract(1, 'year').startOf('month'),
      endDate: dayjs().add(3, 'year').startOf('month')
    };
  }

  if (!startDate || !startDate.isValid() || !endDate || !endDate.isValid()) {
    console.error('Hesaplanan tarih aralığı geçersiz!', {
      startDate: startDate?.format?.('YYYY-MM-DD') || startDate,
      endDate: endDate?.format?.('YYYY-MM-DD') || endDate,
      startDatesCount: startDates.length,
      endDatesCount: endDates.length
    });
    return {
      // 13.08 düzeltmesi: sabit (bayat) pencere yerine bugüne göre dinamik pencere
      startDate: dayjs().subtract(1, 'year').startOf('month'),
      endDate: dayjs().add(3, 'year').startOf('month')
    };
  }

  if (startDate.isAfter(endDate)) {
    // 13.08 düzeltmesi: verideki başlangıç > bitiş kayıtları (sözleşme tarihleri
    // hatalı geri bildirimi) grafikleri alakasız sabit bir pencereye düşürüyordu.
    // Geçerli başlangıçtan türetilen pencere kullanılır ve bayrakla işaretlenir.
    console.error('Başlangıç tarihi bitiş tarihinden sonra! (bozuk sözleşme tarihi verisi)', {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD')
    });
    return { startDate, endDate: startDate.add(4, 'year'), invalidRange: true };
  }

  return { startDate, endDate };
};

export const generateDateArray = (startDate, endDate) => {
  const dates = [];
  let currentDate = dayjs(startDate).startOf('month');
  const end = dayjs(endDate).startOf('month');

  if (!currentDate.isValid() || !end.isValid()) {
    console.error('Geçersiz tarih aralığı!', { startDate, endDate });
    return [];
  }

  if (currentDate.isAfter(end)) {
    console.warn('Başlangıç tarihi bitiş tarihinden sonra!');
    return [];
  }

  while (currentDate.isBefore(end) || currentDate.isSame(end)) {
    dates.push(currentDate.toDate());
    currentDate = currentDate.add(1, 'month');
  }

  return dates;
};

export const generateProjection = (bankContracts, rentContracts, startDate, endDate, inflationRates = {}) => {
  const getInflationRateFn = (rateType, date) => getInflationRate(rateType, date, inflationRates);
  const dates = generateDateArray(startDate, endDate);
  return calculateProjectionForDates(dates, bankContracts, rentContracts, getInflationRateFn);
};